import { DashReferralSourceApi, IDashDynamicRegPage, IDashGenericDynamicPage, useDynamicRegPageApi, useGenericDynamicPageApi } from "@nkr/models/dash";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, FormControl, Row, Well } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useForm, useAppHookContext } from "ClientApp/utils/rest-api";
import { ActionCreators as AuthActionCreators } from "@nkr/store/authorization/actions";
import FileUpload from "ClientApp/components/file-upload";

interface IPageEditorPageProps {
    mode?: "source" | "generic",
    sourceId?: string | number,
    pageId?: string | number,
    embedded?: boolean,
    onSaved?: (page: IDashDynamicRegPage | IDashGenericDynamicPage, isNewPage: boolean) => void,
    onDeleted?: (deletedPageId: number, wasActive: boolean) => void,
    currentPageData?: IDashDynamicRegPage | IDashGenericDynamicPage | null,
}

const GENERIC_HERO_TEXT_OVERLAY_DEFAULT = "Take the first step and see if you are qualified to donate a kidney.";

const emptyPage = (entryReferrerId: number, mode: "source" | "generic"): IDashDynamicRegPage => ({
    id: 0,
    entryReferrerId,
    name: "",
    referralSourceCode: "",
    referralSourceDisplayName: "",
    createdDate: "",
    isActive: false,
    heroRibbonImageUrlEn: "",
    heroRibbonImageUrlEs: "",
    heroTextOverlayEn: mode === "generic" ? GENERIC_HERO_TEXT_OVERLAY_DEFAULT : "",
    heroTextOverlayEs: "",
    heroTextOverlayAlignment: "left",
    ribbonTwoMediaUrlEn: "",
    ribbonTwoMediaUrlEs: "",
    ribbonTwoTitleEn: "",
    ribbonTwoTitleEs: "",
    ribbonTwoIntroParagraphEn: "",
    ribbonTwoIntroParagraphEs: "",
    ribbonTwoBodyParagraphEn: "",
    ribbonTwoBodyParagraphEs: "",
});

const TITLE_MAX = 80;
const HERO_TEXT_OVERLAY_MAX = 75;

const extractImageFilename = (value?: string | null) => {
    if (!value) return null;

    const trimmed = value.trim();
    if (!trimmed) return null;

    if (trimmed.startsWith("blob:") || trimmed.startsWith("data:")) {
        return trimmed;
    }

    let filename = trimmed;

    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
        try {
            const url = new URL(trimmed);
            filename = url.searchParams.get("filename")
                || url.pathname.split("/").filter(Boolean).pop()
                || trimmed;
        } catch {
            filename = trimmed.split("/").filter(Boolean).pop() || trimmed;
        }
    } else if (trimmed.startsWith("/")) {
        filename = trimmed.split("/").filter(Boolean).pop() || trimmed;
    }

    return filename;
};

const buildProtectedImageUrl = (mode: "source" | "generic", filename: string) =>
    mode === "generic"
        ? `${__API__}/dash/generic-pages/image?filename=${encodeURIComponent(filename)}`
        : `${__API__}/dash/referral-sources/pages/image?filename=${encodeURIComponent(filename)}`;

const useFilePreviewUrl = (file?: File | null) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!file) {
            setPreviewUrl(null);
            return;
        }

        const nextUrl = URL.createObjectURL(file);
        setPreviewUrl(nextUrl);

        return () => {
            URL.revokeObjectURL(nextUrl);
        };
    }, [file]);

    return previewUrl;
};

const useProtectedImageSrc = (mode: "source" | "generic", value?: string | null) => {
    const { dispatch } = useAppHookContext();
    const [imageSrc, setImageSrc] = useState<string | null>(null);

    useEffect(() => {
        if (!value) {
            setImageSrc(null);
            return;
        }

        const trimmed = value.trim();
        if (!trimmed) {
            setImageSrc(null);
            return;
        }

        if (trimmed.startsWith("blob:") || trimmed.startsWith("data:")) {
            setImageSrc(trimmed);
            return;
        }

        const filename = extractImageFilename(trimmed);
        if (!filename || filename.startsWith("blob:") || filename.startsWith("data:")) {
            setImageSrc(filename);
            return;
        }

        let isDisposed = false;
        let objectUrl: string | null = null;

        dispatch(AuthActionCreators.apiGet({
            url: buildProtectedImageUrl(mode, filename),
            responseType: "blob",
        }))
            .then((response: { data?: Blob }) => {
                if (isDisposed || !response?.data) {
                    return;
                }

                objectUrl = URL.createObjectURL(response.data);
                setImageSrc(objectUrl);
            })
            .catch((error: any) => {
                if (!isDisposed) {
                    console.error("Unable to load protected image.", error);
                    setImageSrc(null);
                }
            });

        return () => {
            isDisposed = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [dispatch, mode, value]);

    return imageSrc;
};

const CharCounter = ({ value, max, recommended }: { value: string, max?: number, recommended?: number }) => {
    const len = value.length;
    const isOver = max != null && len > max;
    const isNearRecommended = recommended != null && len > recommended;
    return (
        <div style={{ textAlign: "right", fontSize: 11, color: isOver ? "#a94442" : isNearRecommended ? "#8a6d3b" : "#999", marginTop: 2 }}>
            {max != null ? `${len}/${max}` : `${len}`}
            {recommended != null && !max && ` (recommended ${recommended})`}
        </div>
    );
};

const PageEditorPage = (props: IPageEditorPageProps) => {
    const mode = props.mode ?? "source";
    const sourceId = Number(props.sourceId ?? 0);
    const pageId = props.pageId != null && props.pageId !== "new" ? Number(props.pageId) : null;
    const pageApi = mode === "generic" ? useGenericDynamicPageApi() : useDynamicRegPageApi(sourceId);
    const sourceResult = DashReferralSourceApi.useGetById(mode === "source" ? sourceId : null);
    const source = sourceResult?.payload;
    const form = useForm<IDashDynamicRegPage | IDashGenericDynamicPage>(pageApi, pageId, null, null, emptyPage(sourceId, mode));
    const { dispatch } = useAppHookContext();
    const deleteHook = pageId ? pageApi.useDelete(pageId) : null;
    const [actionMessage, setActionMessage] = useState("");
    const [previewLanguage, setPreviewLanguage] = useState<"en" | "es">("en");
    const [heroImageFiles, setHeroImageFiles] = useState<File[]>([]);
    const [heroImageEsFiles, setHeroImageEsFiles] = useState<File[]>([]);
    const [ribbonTwoMediaFiles, setRibbonTwoMediaFiles] = useState<File[]>([]);
    const [ribbonTwoMediaEsFiles, setRibbonTwoMediaEsFiles] = useState<File[]>([]);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const savedPageRef = useRef<IDashDynamicRegPage | IDashGenericDynamicPage | null>(null);
    const isEmbedded = props.embedded === true;
    const currentPageData = props.currentPageData ?? null;

    const pageTitle = useMemo(() => {
        const titlePrefix = mode === "generic" ? "Generic Landing Page" : "Dynamic Registration Page";
        return pageId ? `Edit ${titlePrefix}` : `Add ${titlePrefix}`;
    }, [mode, pageId]);

    const heroImagePreviewUrl = useFilePreviewUrl(heroImageFiles[0]);
    const heroImageEsPreviewUrl = useFilePreviewUrl(heroImageEsFiles[0]);
    const ribbonTwoMediaPreviewUrl = useFilePreviewUrl(ribbonTwoMediaFiles[0]);
    const ribbonTwoMediaEsPreviewUrl = useFilePreviewUrl(ribbonTwoMediaEsFiles[0]);

    const previewHeroValue = useMemo(() => {
        if (previewLanguage === "es") {
            if (heroImageEsPreviewUrl) return heroImageEsPreviewUrl;
            if (form.data?.heroRibbonImageUrlEs) return form.data.heroRibbonImageUrlEs;
        }
        if (heroImagePreviewUrl) return heroImagePreviewUrl;
        return form.data?.heroRibbonImageUrlEn || null;
    }, [previewLanguage, heroImagePreviewUrl, heroImageEsPreviewUrl, form.data?.heroRibbonImageUrlEn, form.data?.heroRibbonImageUrlEs]);

    const previewRibbonMediaValue = useMemo(() => {
        if (previewLanguage === "es") {
            if (ribbonTwoMediaEsPreviewUrl) return ribbonTwoMediaEsPreviewUrl;
            if (form.data?.ribbonTwoMediaUrlEs) return form.data.ribbonTwoMediaUrlEs;
        }
        if (ribbonTwoMediaPreviewUrl) return ribbonTwoMediaPreviewUrl;
        return form.data?.ribbonTwoMediaUrlEn || null;
    }, [previewLanguage, ribbonTwoMediaPreviewUrl, ribbonTwoMediaEsPreviewUrl, form.data?.ribbonTwoMediaUrlEn, form.data?.ribbonTwoMediaUrlEs]);

    const previewHeroSrc = useProtectedImageSrc(mode, previewHeroValue);
    const previewRibbonMediaSrc = useProtectedImageSrc(mode, previewRibbonMediaValue);
    const currentHeroRibbonImageUrlEn = useProtectedImageSrc(mode, form.data?.heroRibbonImageUrlEn);
    const currentHeroRibbonImageUrlEs = useProtectedImageSrc(mode, form.data?.heroRibbonImageUrlEs);
    const currentRibbonTwoMediaUrlEn = useProtectedImageSrc(mode, form.data?.ribbonTwoMediaUrlEn);
    const currentRibbonTwoMediaUrlEs = useProtectedImageSrc(mode, form.data?.ribbonTwoMediaUrlEs);

    const previewHeroTextOverlay = useMemo(
        () => previewLanguage === "es" && form.data?.heroTextOverlayEs ? form.data.heroTextOverlayEs : form.data?.heroTextOverlayEn,
        [form.data?.heroTextOverlayEn, form.data?.heroTextOverlayEs, previewLanguage]
    );
    const previewHeroTextOverlayAlignment = form.data?.heroTextOverlayAlignment === "right" ? "right" : "left";

    const previewTitle = useMemo(
        () => previewLanguage === "es" && form.data?.ribbonTwoTitleEs ? form.data.ribbonTwoTitleEs : form.data?.ribbonTwoTitleEn,
        [form.data?.ribbonTwoTitleEn, form.data?.ribbonTwoTitleEs, previewLanguage]
    );
    const previewIntro = useMemo(
        () => previewLanguage === "es" && form.data?.ribbonTwoIntroParagraphEs ? form.data.ribbonTwoIntroParagraphEs : form.data?.ribbonTwoIntroParagraphEn,
        [form.data?.ribbonTwoIntroParagraphEn, form.data?.ribbonTwoIntroParagraphEs, previewLanguage]
    );
    const previewBody = useMemo(
        () => previewLanguage === "es" && form.data?.ribbonTwoBodyParagraphEs ? form.data.ribbonTwoBodyParagraphEs : form.data?.ribbonTwoBodyParagraphEn,
        [form.data?.ribbonTwoBodyParagraphEn, form.data?.ribbonTwoBodyParagraphEs, previewLanguage]
    );

    useEffect(() => {
        if (mode !== "source" || !source || pageId !== null) {
            return;
        }

        form.replaceData({
            ...form.data,
            entryReferrerId: sourceId,
            referralSourceCode: source.srcCode ?? "",
            referralSourceDisplayName: source.description ?? "",
        }, { baseline: false, dirty: form.isDirty });
    }, [mode, source?.id]);

    useEffect(() => {
        if (!pageId || !currentPageData || !form.data || form.data.id !== currentPageData.id) {
            return;
        }

        if (form.data.isActive === currentPageData.isActive) {
            return;
        }

        form.replaceData({
            ...form.data,
            isActive: currentPageData.isActive,
        }, { baseline: false, dirty: form.isDirty });
    }, [currentPageData?.id, currentPageData?.isActive, pageId, form.data?.id, form.data?.isActive, form.isDirty]);
    form.setOnSaveComplete((saved) => {
        savedPageRef.current = saved;
        setActionMessage(saved.isActive ? "Page saved as active." : "Page saved.");
    });

    const uploadMediaFiles = async (savedPageId: number): Promise<IDashDynamicRegPage | IDashGenericDynamicPage | null> => {
        if (heroImageFiles.length === 0 && heroImageEsFiles.length === 0 && ribbonTwoMediaFiles.length === 0 && ribbonTwoMediaEsFiles.length === 0) {
            return null;
        }

        try {
            setIsUploadingMedia(true);
            const formData = new FormData();

            if (heroImageFiles.length > 0) formData.append("heroImage", heroImageFiles[0]);
            if (heroImageEsFiles.length > 0) formData.append("heroImageEs", heroImageEsFiles[0]);
            if (ribbonTwoMediaFiles.length > 0) formData.append("ribbonTwoMedia", ribbonTwoMediaFiles[0]);
            if (ribbonTwoMediaEsFiles.length > 0) formData.append("ribbonTwoMediaEs", ribbonTwoMediaEsFiles[0]);

            const response = await dispatch(AuthActionCreators.apiPostFormData({
                url: mode === "generic"
                    ? `${__API__}/dash/generic-pages/${savedPageId}/upload-media`
                    : `${__API__}/dash/referral-sources/${sourceId}/pages/${savedPageId}/upload-media`,
                data: formData,
                headers: [{ name: "Accept", value: "application/json" }],
            })) as { data?: any };

            const result = response?.data;
            if ((result.success || result.isSuccess) && result.payload) {
                form.replaceData(result.payload, { baseline: true, dirty: false });
                pageApi.refreshCachedEntity(result.payload);
                setHeroImageFiles([]);
                setHeroImageEsFiles([]);
                setRibbonTwoMediaFiles([]);
                setRibbonTwoMediaEsFiles([]);
                return result.payload;
            }

            setActionMessage(result?.message || "Upload failed: Invalid response");
            return null;
        } catch (error) {
            setActionMessage(`Upload error: ${error instanceof Error ? error.message : "Unknown error"}`);
            return null;
        } finally {
            setIsUploadingMedia(false);
        }
    };

    const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const saved = await form.handleSubmit(e);
        if (!saved) return;

        const savedPage = savedPageRef.current;
        if (!savedPage?.id) return;

        let finalPayload = savedPage;
        if (heroImageFiles.length > 0 || heroImageEsFiles.length > 0 || ribbonTwoMediaFiles.length > 0 || ribbonTwoMediaEsFiles.length > 0) {
            const uploaded = await uploadMediaFiles(savedPage.id);
            if (uploaded) {
                finalPayload = uploaded;
            }
        }

        props.onSaved?.(finalPayload, pageId === null);
        if (!isEmbedded && mode === "source" && !pageId && finalPayload.id) {
            window.history.replaceState(null, "", `/admin/dash-referral-sources/referral-source/${sourceId}/pages/${finalPayload.id}`);
        }
    };

    const clearPageImage = (
        fieldName: "heroRibbonImageUrlEn" | "heroRibbonImageUrlEs" | "ribbonTwoMediaUrlEn" | "ribbonTwoMediaUrlEs",
        clearPendingFiles: () => void,
    ) => {
        clearPendingFiles();
        form.setField(fieldName, "");
        setActionMessage("Image will be removed when you save the page.");
    };

    const deletePage = async () => {
        if (!deleteHook || !pageId) return;
        if (!window.confirm("Delete this page version?")) return;

        const result = await deleteHook.submit();
        if (result?.isSuccess) {
            props.onDeleted?.(pageId, form.data?.isActive ?? false);
            if (isEmbedded) return;
            window.location.href = "/admin/dash-referral-sources";
            return;
        }

        setActionMessage(result?.message || "Unable to delete page.");
    };

    return (
        <div style={{ padding: isEmbedded ? 0 : "12px 15px" }}>
            {!isEmbedded && (
                <Row style={{ marginBottom: 12 }}>
                    <Col xs={12} md={8}>
                        <h3 style={{ margin: 0 }}>{pageTitle}</h3>
                        <div style={{ marginTop: 6 }}>
                            <Link to="/admin/dash-referral-sources">Back to DASH Referral Sources</Link>
                        </div>
                    </Col>
                </Row>
            )}

            <Well>
                {mode === "source" ? (
                    <Row style={{ marginBottom: 12 }}>
                        <Col xs={12} md={6}>
                            <strong>Referral Source</strong>
                            <div>{source?.description || form.data?.referralSourceDisplayName || "--"}</div>
                        </Col>
                        <Col xs={12} md={3}>
                            <strong>SRC Code</strong>
                            <div>{source?.srcCode || form.data?.referralSourceCode || "--"}</div>
                        </Col>
                        <Col xs={12} md={3}>
                            <strong>Page Status</strong>
                            <div>{pageId ? "Existing Page" : "New Page"}</div>
                        </Col>
                    </Row>
                ) : (
                    <Row style={{ marginBottom: 12 }}>
                        <Col xs={12} md={8}>
                            <strong>Landing Page Type</strong>
                            <div>Generic fallback page</div>
                        </Col>
                        <Col xs={12} md={4}>
                            <strong>Page Status</strong>
                            <div>{pageId ? "Existing Page" : "New Page"}</div>
                        </Col>
                    </Row>
                )}

                {actionMessage && <Alert bsStyle="info">{actionMessage}</Alert>}
                {!form.isLoaded && pageId && "Loading..."}
                {form.data && (
                    <form id="dash-dynamic-page-editor" onSubmit={handleFormSubmit}>
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12}><strong>Version Name</strong></Col>
                            <Col xs={12}><FormControl required name="name" type="text" value={form.data.name ?? ""} onChange={form.handleStringChange} /></Col>
                        </Row>

                        {/* Hero Ribbon Images */}
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12} md={6}>
                                <strong>Hero Ribbon Image (EN)</strong>
                                <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>1400 × 500 px, .jpg or .webp</div>
                                <FileUpload
                                    label="Drop image or click to select"
                                    files={heroImageFiles}
                                    accept=".jpg,.jpeg,.webp"
                                    singleUploadOnly={true}
                                    onFileDropped={(files) => setHeroImageFiles(files)}
                                    onRemoveFile={() => setHeroImageFiles([])}
                                    height="60px"
                                />
                                {form.data.heroRibbonImageUrlEn && heroImageFiles.length === 0 && (
                                    <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
                                        Current: {currentHeroRibbonImageUrlEn
                                            ? <a href={currentHeroRibbonImageUrlEn} target="_blank" rel="noopener noreferrer">View</a>
                                            : "Loading..."}
                                        <Button type="button" bsSize="xsmall" style={{ marginLeft: 8 }} onClick={() => clearPageImage("heroRibbonImageUrlEn", () => setHeroImageFiles([]))}>Remove</Button>
                                    </div>
                                )}
                            </Col>
                            <Col xs={12} md={6}>
                                <strong>Hero Ribbon Image (ES) <span style={{ fontWeight: "normal", color: "#888" }}>optional</span></strong>
                                <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>1400 × 500 px, .jpg or .webp</div>
                                <FileUpload
                                    label="Drop image or click to select"
                                    files={heroImageEsFiles}
                                    accept=".jpg,.jpeg,.webp"
                                    singleUploadOnly={true}
                                    onFileDropped={(files) => setHeroImageEsFiles(files)}
                                    onRemoveFile={() => setHeroImageEsFiles([])}
                                    height="60px"
                                />
                                {form.data.heroRibbonImageUrlEs && heroImageEsFiles.length === 0 && (
                                    <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
                                        Current: {currentHeroRibbonImageUrlEs
                                            ? <a href={currentHeroRibbonImageUrlEs} target="_blank" rel="noopener noreferrer">View</a>
                                            : "Loading..."}
                                        <Button type="button" bsSize="xsmall" style={{ marginLeft: 8 }} onClick={() => clearPageImage("heroRibbonImageUrlEs", () => setHeroImageEsFiles([]))}>Remove</Button>
                                    </div>
                                )}
                            </Col>
                        </Row>

                        {/* Hero Text Overlay */}
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12} md={5}>
                                <strong>Hero Text Overlay (EN) <span style={{ fontWeight: "normal", color: "#888" }}>optional</span></strong>
                                <FormControl name="heroTextOverlayEn" type="text" maxLength={HERO_TEXT_OVERLAY_MAX} value={form.data.heroTextOverlayEn ?? ""} onChange={form.handleStringChange} />
                                <CharCounter value={form.data.heroTextOverlayEn ?? ""} max={HERO_TEXT_OVERLAY_MAX} />
                            </Col>
                            <Col xs={12} md={5}>
                                <strong>Hero Text Overlay (ES) <span style={{ fontWeight: "normal", color: "#888" }}>optional</span></strong>
                                <FormControl name="heroTextOverlayEs" type="text" maxLength={HERO_TEXT_OVERLAY_MAX} value={form.data.heroTextOverlayEs ?? ""} onChange={form.handleStringChange} />
                                <CharCounter value={form.data.heroTextOverlayEs ?? ""} max={HERO_TEXT_OVERLAY_MAX} />
                            </Col>
                            <Col xs={12} md={2}>
                                <strong>Alignment</strong>
                                <FormControl
                                    componentClass="select"
                                    name="heroTextOverlayAlignment"
                                    value={form.data.heroTextOverlayAlignment ?? "left"}
                                    onChange={form.handleStringChange}
                                    disabled={!form.data.heroTextOverlayEn && !form.data.heroTextOverlayEs}
                                >
                                    <option value="left">Left</option>
                                    <option value="right">Right</option>
                                </FormControl>
                            </Col>
                        </Row>

                        {/* Ribbon Two Title */}
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12} md={6}>
                                <strong>Ribbon Two Title (EN)</strong>
                                <FormControl name="ribbonTwoTitleEn" type="text" maxLength={TITLE_MAX} value={form.data.ribbonTwoTitleEn ?? ""} onChange={form.handleStringChange} />
                                <CharCounter value={form.data.ribbonTwoTitleEn ?? ""} max={TITLE_MAX} />
                            </Col>
                            <Col xs={12} md={6}>
                                <strong>Ribbon Two Title (ES) <span style={{ fontWeight: "normal", color: "#888" }}>optional</span></strong>
                                <FormControl name="ribbonTwoTitleEs" type="text" maxLength={TITLE_MAX} value={form.data.ribbonTwoTitleEs ?? ""} onChange={form.handleStringChange} />
                                <CharCounter value={form.data.ribbonTwoTitleEs ?? ""} max={TITLE_MAX} />
                            </Col>
                        </Row>

                        {/* Ribbon Two Intro */}
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12} md={6}>
                                <strong>Ribbon Two Intro (EN)</strong>
                                <FormControl componentClass="textarea" rows={4} name="ribbonTwoIntroParagraphEn" value={form.data.ribbonTwoIntroParagraphEn ?? ""} onChange={form.handleStringChange} />
                                <CharCounter value={form.data.ribbonTwoIntroParagraphEn ?? ""} recommended={250} />
                            </Col>
                            <Col xs={12} md={6}>
                                <strong>Ribbon Two Intro (ES) <span style={{ fontWeight: "normal", color: "#888" }}>optional</span></strong>
                                <FormControl componentClass="textarea" rows={4} name="ribbonTwoIntroParagraphEs" value={form.data.ribbonTwoIntroParagraphEs ?? ""} onChange={form.handleStringChange} />
                                <CharCounter value={form.data.ribbonTwoIntroParagraphEs ?? ""} recommended={250} />
                            </Col>
                        </Row>

                        {/* Ribbon Two Body */}
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12} md={6}>
                                <strong>Ribbon Two Body (EN)</strong>
                                <FormControl componentClass="textarea" rows={6} name="ribbonTwoBodyParagraphEn" value={form.data.ribbonTwoBodyParagraphEn ?? ""} onChange={form.handleStringChange} />
                                <CharCounter value={form.data.ribbonTwoBodyParagraphEn ?? ""} recommended={250} />
                            </Col>
                            <Col xs={12} md={6}>
                                <strong>Ribbon Two Body (ES) <span style={{ fontWeight: "normal", color: "#888" }}>optional</span></strong>
                                <FormControl componentClass="textarea" rows={6} name="ribbonTwoBodyParagraphEs" value={form.data.ribbonTwoBodyParagraphEs ?? ""} onChange={form.handleStringChange} />
                                <CharCounter value={form.data.ribbonTwoBodyParagraphEs ?? ""} recommended={250} />
                            </Col>
                        </Row>

                        {/* Ribbon Two Images */}
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12} md={6}>
                                <strong>Ribbon Two Image (EN)</strong>
                                <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Max 470 px wide, .jpg or .webp</div>
                                <FileUpload
                                    label="Drop image or click to select"
                                    files={ribbonTwoMediaFiles}
                                    accept=".jpg,.jpeg,.webp"
                                    singleUploadOnly={true}
                                    onFileDropped={(files) => setRibbonTwoMediaFiles(files)}
                                    onRemoveFile={() => setRibbonTwoMediaFiles([])}
                                    height="60px"
                                />
                                {form.data.ribbonTwoMediaUrlEn && ribbonTwoMediaFiles.length === 0 && (
                                    <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
                                        Current: {currentRibbonTwoMediaUrlEn
                                            ? <a href={currentRibbonTwoMediaUrlEn} target="_blank" rel="noopener noreferrer">View</a>
                                            : "Loading..."}
                                        <Button type="button" bsSize="xsmall" style={{ marginLeft: 8 }} onClick={() => clearPageImage("ribbonTwoMediaUrlEn", () => setRibbonTwoMediaFiles([]))}>Remove</Button>
                                    </div>
                                )}
                            </Col>
                            <Col xs={12} md={6}>
                                <strong>Ribbon Two Image (ES) <span style={{ fontWeight: "normal", color: "#888" }}>optional</span></strong>
                                <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Max 470 px wide, .jpg or .webp</div>
                                <FileUpload
                                    label="Drop image or click to select"
                                    files={ribbonTwoMediaEsFiles}
                                    accept=".jpg,.jpeg,.webp"
                                    singleUploadOnly={true}
                                    onFileDropped={(files) => setRibbonTwoMediaEsFiles(files)}
                                    onRemoveFile={() => setRibbonTwoMediaEsFiles([])}
                                    height="60px"
                                />
                                {form.data.ribbonTwoMediaUrlEs && ribbonTwoMediaEsFiles.length === 0 && (
                                    <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
                                        Current: {currentRibbonTwoMediaUrlEs
                                            ? <a href={currentRibbonTwoMediaUrlEs} target="_blank" rel="noopener noreferrer">View</a>
                                            : "Loading..."}
                                        <Button type="button" bsSize="xsmall" style={{ marginLeft: 8 }} onClick={() => clearPageImage("ribbonTwoMediaUrlEs", () => setRibbonTwoMediaEsFiles([]))}>Remove</Button>
                                    </div>
                                )}
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={4}>
                                <Button
                                    bsStyle="primary"
                                    type="submit"
                                    disabled={isUploadingMedia || form.isMutating}
                                >
                                    {isUploadingMedia ? "Uploading..." : pageId ? "Update Page" : "Create Page"}
                                </Button>
                            </Col>
                            {pageId && (
                                <Col xs={12} md={4}>
                                    <Button type="button" bsStyle="danger" onClick={deletePage}>
                                        Delete Page
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    </form>
                )}
            </Well>

            {/* Page Preview */}
            <Well id="dash-dynamic-page-preview">
                <Row style={{ marginBottom: 12 }}>
                    <Col xs={12} md={8}>
                        <h4 style={{ margin: 0 }}>Page Preview</h4>
                        <div style={{ marginTop: 6, color: "#666" }}>
                            Previewing the dynamic content block as it will appear on the registration page.
                        </div>
                    </Col>
                    <Col xs={12} md={4} style={{ textAlign: "right" }}>
                        <FormControl
                            componentClass="select"
                            value={previewLanguage}
                            onChange={(e: FormEvent<FormControl & HTMLInputElement>) =>
                                setPreviewLanguage(e.currentTarget.value as "en" | "es")}
                            style={{ maxWidth: 180, marginLeft: "auto" }}
                        >
                            <option value="en">Preview English</option>
                            <option value="es">Preview Spanish</option>
                        </FormControl>
                    </Col>
                </Row>

                <div style={{ background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 6, padding: 24 }}>
                    {previewHeroSrc && (
                        <div style={{ marginBottom: 20, position: "relative" }}>
                            <img
                                src={previewHeroSrc ?? undefined}
                                alt="Registration page hero preview"
                                style={{ width: "100%", maxHeight: 320, objectFit: "cover" }}
                            />
                            {previewHeroTextOverlay && (
                                <div style={{
                                    position: "absolute", inset: 0, display: "flex", alignItems: "center",
                                    justifyContent: previewHeroTextOverlayAlignment === "right" ? "flex-end" : "flex-start",
                                    padding: 24, pointerEvents: "none",
                                }}>
                                    <p style={{
                                        maxWidth: "60%", margin: 0, color: "#fff", fontWeight: 700,
                                        fontSize: 24, lineHeight: 1.2, textShadow: "0 3px 14px rgba(0, 0, 0, 0.55)",
                                        textAlign: previewHeroTextOverlayAlignment === "right" ? "right" : "left",
                                    }}>
                                        {previewHeroTextOverlay}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {(!!previewRibbonMediaSrc || !!previewTitle || !!previewIntro || !!previewBody) && (
                        <div style={{ background: "#fff", border: "1px solid #ddd", borderRadius: 6, padding: 24 }}>
                            <Row>
                                <Col xs={12} md={previewRibbonMediaSrc ? 8 : 12}>
                                    <h2 style={{ marginTop: 0 }}>{previewTitle || "Ribbon title preview"}</h2>
                                    <p style={{ whiteSpace: "pre-line" }}>{previewIntro || "Ribbon intro preview"}</p>
                                    <p style={{ whiteSpace: "pre-line", marginBottom: 0 }}>{previewBody || "Ribbon body preview"}</p>
                                </Col>
                                {previewRibbonMediaSrc && (
                                    <Col xs={12} md={4} style={{ marginBottom: 15 }}>
                                        <img
                                            src={previewRibbonMediaSrc ?? undefined}
                                            alt="Registration page secondary media preview"
                                            style={{ width: "100%", borderRadius: 6, background: "#f8f8f8" }}
                                        />
                                    </Col>
                                )}
                            </Row>
                        </div>
                    )}

                    {!previewHeroSrc && !previewRibbonMediaSrc && !previewTitle && !previewIntro && !previewBody && (
                        <div style={{ color: "#777" }}>
                            Add content above to preview the dynamic registration page section.
                        </div>
                    )}
                </div>
            </Well>
        </div>
    );
};

export default PageEditorPage;




